import { format } from 'date-fns'
import { careColor } from '@/lib/care-palette'
import { cn } from '@/lib/utils'
import type { CareSectionSummary } from '@/types/care'

interface CareSymptomTableProps {
  summaries: CareSectionSummary[]
  /** The check-in the rows describe. */
  date: Date | null
  className?: string
}

/** The red-stroked pill's colour — a symptom that has newly reached its section's worst level. */
const WORSE_COLOR = '#E5484D'

export default function CareSymptomTable({ summaries, date, className }: CareSymptomTableProps) {
  const withSymptoms = summaries.filter(summary => summary.symptoms.length > 0 || summary.value !== null)
  if (withSymptoms.length === 0) return null

  return (
    <div className={cn('flex flex-col gap-6 font-poppins', className)}>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='flex flex-col gap-1'>
          <h3 className='text-xl font-semibold text-custom-neutral-600'>Latest Care Consultation</h3>
          {date && <p className='text-base text-custom-neutral-800'>{format(date, 'd MMMM yyyy')}</p>}
          <p className='text-sm text-custom-neutral-400'>
            Existing or worsening symptoms reported by the patient during their care consultation
          </p>
        </div>

        <div className='flex flex-col gap-2 text-sm'>
          <span className='flex items-center gap-2 text-custom-neutral-800'>
            <span className='h-5 w-5 shrink-0 rounded-full bg-custom-neutral-200' aria-hidden='true' />
            Existing symptom
          </span>
          <span className='flex items-center gap-2 text-custom-neutral-800'>
            <span
              className='h-5 w-5 shrink-0 rounded-full border-2'
              style={{ borderColor: WORSE_COLOR }}
              aria-hidden='true'
            />
            Symptom worse
          </span>
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        {withSymptoms.map(summary => (
          <div key={summary.key} className='flex items-start gap-4 rounded-2xl bg-[#FAFAFB] px-5 py-4'>
            <div className='flex flex-1 flex-col gap-3'>
              <span className='flex items-center gap-2 text-base font-medium text-custom-neutral-800'>
                <span
                  className='h-2.5 w-2.5 shrink-0 rounded-full'
                  style={{ backgroundColor: careColor(summary.color) }}
                  aria-hidden='true'
                />
                {summary.name}
              </span>

              {summary.symptoms.length > 0 ? (
                <ul className='flex flex-wrap gap-2'>
                  {summary.symptoms.map(symptom => (
                    <li
                      key={symptom.questionHash}
                      title={symptom.isWorse ? 'Scored higher than at the previous care consultation' : undefined}
                      className={cn(
                        'rounded-full px-4 py-1.5 text-sm',
                        symptom.isWorse ? 'border-2 bg-white' : 'bg-custom-neutral-200 text-custom-neutral-800'
                      )}
                      style={symptom.isWorse ? { borderColor: WORSE_COLOR, color: WORSE_COLOR } : undefined}
                    >
                      {symptom.label}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='text-sm text-custom-neutral-400'>No symptoms reported in this section</p>
              )}
            </div>

            <span className='shrink-0 text-lg font-semibold text-custom-neutral-800'>
              {summary.value !== null ? `${summary.value}${summary.max ? `/${summary.max}` : ''}` : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
